import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Globe, Inbox } from "lucide-react";
import api, { money, dateShort, timeAgo, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MetricCard, PageHeader, Pill, Progress, Thumb, SectionTitle, EmptyState, Cover } from "@/components/Bits";

export default function Dashboard() {
  const { user, isStaff } = useAuth();
  const [data, setData] = useState(null);
  const [attention, setAttention] = useState(null);
  const [billing, setBilling] = useState(null);
  const [inquiries, setInquiries] = useState(null);
  const [website, setWebsite] = useState(null);
  const [websiteError, setWebsiteError] = useState("");
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/admin/action-center").then((r) => setAttention(r.data)).catch(() => {});
    api.get("/invoices/summary").then((r) => setBilling(r.data)).catch(() => {});
    api.get("/calendar/upcoming").then((r) => setUpcoming(r.data || [])).catch(() => {});
    if (isStaff) {
      api.get("/inquiries/summary").then((r) => setInquiries(r.data))
        .catch((e) => setWebsiteError(apiError(e)));
      api.get("/content/overview").then((r) => setWebsite(r.data))
        .catch((e) => setWebsiteError(apiError(e)));
    }
  }, [isStaff]);

  const firstName = (user?.name || "there").split(" ")[0];
  const m = data?.metrics || {};

  return (
    <div className="space-y-14" data-testid="dashboard-page">
      <PageHeader
        overline="Studio Overview"
        title={`Good morning, ${firstName}`}
        subtitle="Here’s what’s happening across your projects."
        actions={
          isStaff && (
            <Link
              data-testid="dashboard-new-project-btn"
              to="/projects/new"
              className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark"
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} /> New Project
            </Link>
          )
        }
      />

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
        <MetricCard testId="metric-active-projects" label="Active Projects" value={m.active_projects ?? "—"} accent to="/projects?status=Active" />
        <MetricCard testId="metric-awaiting-approval" label="Awaiting Client Approval" value={m.awaiting_client_approval ?? "—"} hint="Selections presented" to="/procurement?selection=Awaiting%20Approval" />
        <MetricCard testId="metric-to-be-ordered" label="To Be Ordered" value={m.to_be_ordered ?? "—"} hint="Approved, not purchased" to="/procurement?status=Ready%20to%20Order" />
        <MetricCard testId="metric-in-transit" label="In Transit" value={m.in_transit ?? "—"} hint="Shipped items" to="/procurement?status=Shipped" />
        <MetricCard testId="metric-upcoming-installs" label="Upcoming Installations" value={m.upcoming_installations ?? "—"} to="/calendar?event_type=Installation&upcoming=1&view=agenda" />
      </div>

      {billing && (
        <section>
          <SectionTitle
            action={<Link to="/invoices" data-testid="dashboard-billing-link" className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">All invoices</Link>}
          >
            Payments
          </SectionTitle>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="dashboard-billing">
            <MetricCard testId="dashboard-metric-invoiced" label="Invoiced" value={money(billing.invoiced)} to="/invoices" />
            <MetricCard testId="dashboard-metric-received" label="Payments Received" value={money(billing.received)} accent to="/invoices?status=Paid" />
            <MetricCard testId="dashboard-metric-outstanding" label="Outstanding" value={money(billing.outstanding)} hint={`${billing.open_count} open`} to="/invoices" />
            <MetricCard testId="dashboard-metric-overdue-invoices" label="Overdue Invoices" value={billing.overdue} to="/invoices?status=Overdue" />
          </div>
        </section>
      )}

      {attention && (
        <section>
          <SectionTitle>Needs Attention</SectionTitle>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-action-center">
            {[
              ["Client Approvals Pending", attention.client_approvals_pending, "/projects", null],
              ["Alternatives Requested", attention.alternatives_requested, "/projects", null],
              ["Client Questions", attention.client_questions, "/projects", null],
              ["Orders Ready to Place", attention.orders_ready, "/procurement?status=Ready%20to%20Order", null],
              ["Overdue Client Decisions", attention.overdue_client_decisions, "/projects", null],
              ["Deliveries Overdue", attention.deliveries_overdue, "/procurement", null],
              ["Changed Since Approval", attention.changed_since_approval, "/procurement", null],
            ]
              .filter(([, count]) => count > 0)
              .map(([label, count, link]) => (
                <Link
                  key={label}
                  to={link}
                  data-testid={`attention-${label.toLowerCase().replace(/\s/g, "-")}`}
                  className="card-surface card-hover flex items-center justify-between gap-4 p-6"
                >
                  <span className="text-sm text-ink">
                    <span className="num mr-2 text-2xl">{count}</span>
                    {label}
                  </span>
                  <span className="text-xs uppercase tracking-[0.15em] text-lime-dark">View →</span>
                </Link>
              ))}
            {[attention.client_approvals_pending, attention.alternatives_requested, attention.client_questions,
              attention.orders_ready, attention.deliveries_overdue, attention.changed_since_approval,
              attention.overdue_client_decisions]
              .every((v) => !v) && (
              <div className="card-surface p-6 text-sm text-fog" data-testid="attention-clear">Nothing needs your attention right now.</div>
            )}
          </div>
          {!!attention.questions?.length && (
            <div className="card-surface mt-5 divide-y divide-line" data-testid="admin-client-questions">
              {attention.questions.map((q) => (
                <div key={q.id} className="px-6 py-4 text-sm">
                  <Link to={`/products/${q.product_id}`} className="text-ink hover:text-lime-dark">
                    {q.product_name} — “{q.body}”
                  </Link>
                  <div className="mt-1 text-xs text-fog">{q.author_name} · {timeAgo(q.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {isStaff && websiteError && !inquiries && !website && (
        <div data-testid="dashboard-website-unavailable" className="flex items-start gap-3 rounded-sm border border-[#E07A5F] bg-[#F6DED6] px-5 py-4 text-sm text-[#A8492E]">
          <Globe className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
          <span>
            Website inquiries and portfolio figures are unavailable right now — {websiteError}{" "}
            Your projects, invoices and payments below are unaffected.
          </span>
        </div>
      )}

      {isStaff && inquiries && (
        <section data-testid="dashboard-inquiries">
          <SectionTitle
            action={<Link to="/inquiries" data-testid="dashboard-inquiries-link" className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">All inquiries</Link>}
          >
            New Business
          </SectionTitle>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard testId="metric-new-inquiries" label="New Inquiries" value={inquiries.new_total} accent to="/inquiries?status=new" />
            <MetricCard testId="metric-open-inquiries" label="In the Pipeline" value={inquiries.open_total} hint="Not yet won or closed" to="/inquiries" />
            <MetricCard testId="metric-proposals-sent" label="Proposals Sent" value={inquiries.counts?.proposal_sent ?? 0} to="/inquiries?status=proposal_sent" />
            <MetricCard testId="metric-converted" label="Converted to Projects" value={inquiries.counts?.converted ?? 0} to="/inquiries?status=converted" />
          </div>
          {!!inquiries.recent?.length && (
            <div className="card-surface mt-5 divide-y divide-line" data-testid="dashboard-recent-inquiries">
              {inquiries.recent.map((i) => (
                <Link key={i.id} to={`/inquiries?open=${i.id}`} data-testid={`dashboard-inquiry-${i.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 transition-colors hover:bg-bone">
                  <div className="flex min-w-0 flex-wrap items-center gap-3">
                    <Inbox className="h-4 w-4 shrink-0 text-lime-dark" strokeWidth={1.5} />
                    <span className="text-sm text-ink">{i.name}</span>
                    <Pill tone="bg-[#EDEFC4] text-ink">{i.status_label}</Pill>
                    {i.project_type && <span className="text-xs text-fog">{i.project_type}</span>}
                  </div>
                  <span className="num text-xs text-fog">{dateShort(i.created_at)}</span>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {!!upcoming.length && (
        <section data-testid="dashboard-upcoming">
          <SectionTitle action={<Link to="/calendar" className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">Full calendar</Link>}>
            Upcoming Dates
          </SectionTitle>
          <div className="card-surface divide-y divide-line">
            {upcoming.slice(0, 6).map((e) => (
              <div key={e.id} data-testid={`dashboard-upcoming-${e.id}`} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
                <div className="min-w-0">
                  <div className="text-sm text-ink">{e.title}</div>
                  <div className="mt-1 text-xs text-fog">{[e.project_name, e.event_type].filter(Boolean).join(" · ")}</div>
                </div>
                <span className="num shrink-0 text-xs text-fog">{dateShort(e.start)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <SectionTitle action={<Link to="/projects" className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">All projects</Link>}>
          Active Projects
        </SectionTitle>
        {data?.projects?.length ? (
          <div className="grid gap-7 md:grid-cols-2 xl:grid-cols-3">
            {data.projects.map((p, i) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                data-testid={`dashboard-project-card-${p.id}`}
                className="card-surface card-hover group animate-rise overflow-hidden"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="aspect-[16/10] overflow-hidden bg-line">
                  <Cover project={p} testId={`dashboard-cover-${p.id}`} className="h-full w-full" imgClassName="transition-transform duration-500 group-hover:scale-[1.03]" />
                </div>
                <Progress value={p.progress} />
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate font-serif text-xl text-ink">{p.name}</h3>
                      <div className="mt-1 truncate text-sm text-stone">{p.client_name || "No client"}</div>
                    </div>
                    <Pill tone="bg-[#EDEFC4] text-ink">{p.phase}</Pill>
                  </div>
                  <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs">
                    <span className="overline">{p.project_type}</span>
                    <span className="num text-stone">{p.progress}% complete</span>
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-4 border-t border-line pt-5">
                    <div>
                      <div className="overline">Budget</div>
                      <div className="num mt-1 text-sm text-ink">{money(p.budget_total)}</div>
                    </div>
                    <div>
                      <div className="overline">Approved</div>
                      <div className="num mt-1 text-sm text-lime-dark">{money(p.approved_spend)}</div>
                    </div>
                  </div>
                  {p.install_date && (
                    <div className="mt-4 text-xs text-fog">Install · {dateShort(p.install_date)}</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState testId="dashboard-no-projects" title="No active projects yet" body="Create your first project to start sourcing." />
        )}
      </section>

      {isStaff && (
        <section>
          <SectionTitle>Procurement Snapshot</SectionTitle>
          <div className="card-surface grid grid-cols-2 divide-line sm:grid-cols-5 lg:grid-cols-10">
            {Object.entries(data?.snapshot || {}).map(([label, count]) => (
              <Link
                key={label}
                to="/procurement"
                data-testid={`snapshot-${label.toLowerCase().replace(/\s/g, "-")}`}
                className="border-b border-r border-line p-5 transition-colors hover:bg-bone last:border-r-0"
              >
                <div className="num text-2xl text-ink">{count}</div>
                <div className="overline mt-2 leading-tight">{label}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {isStaff && website && (
        <section data-testid="dashboard-website">
          <SectionTitle
            action={<Link to="/content" data-testid="dashboard-content-link" className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">Content Studio</Link>}
          >
            Public Website
          </SectionTitle>
          <div className="card-surface p-6">
            <div className="mb-5 flex items-center gap-2.5">
              <Globe className="h-4 w-4 text-lime-dark" strokeWidth={1.5} />
              <span className="overline">asthetik.design · portfolio, not client project data</span>
            </div>
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
              {[
                ["Published", website.portfolio_published],
                ["Drafts", website.portfolio_draft],
                ["Featured", website.portfolio_featured],
                ["Testimonials", website.testimonials],
                ["Media items", website.media_items],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="num text-2xl text-ink">{value}</div>
                  <div className="overline mt-1 leading-tight">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section>
        <SectionTitle>Recent Activity</SectionTitle>
        <div className="card-surface divide-y divide-line" data-testid="dashboard-activity">
          {(data?.activity || []).map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
              <div className="min-w-0">
                <div className="truncate text-sm text-ink">
                  <span className="font-medium">{a.user_name}</span> {a.action}
                  {a.entity_name && a.action.indexOf(a.entity_name) === -1 && <span className="text-stone"> — {a.entity_name}</span>}
                </div>
                {a.detail && <div className="mt-1 text-xs text-fog">{a.detail}</div>}
              </div>
              <div className="flex items-center gap-4 text-xs text-fog">
                <span>{a.project_name}</span>
                <span className="num">{timeAgo(a.created_at)}</span>
              </div>
            </div>
          ))}
          {!data?.activity?.length && <div className="px-6 py-10 text-center text-sm text-fog">No activity yet.</div>}
        </div>
      </section>
    </div>
  );
}
